// Archetype quiz: screen generation + navigation. Content in archetype-data.js, scoring in archetype-scoring.js.
import { PATHS, QUESTIONS, RESULTS, EMBLEMS, STORE_LINKS, ARCHETYPE_SLUGS, ARCHETYPES, COMPLEMENT } from './archetype-data.js';
import { scoreArchetype } from './archetype-scoring.js';
import { ARCH_ILLOS } from './illos-archetype.js';
import { shareResult, buildShareUrl, flashCopied, renderCard } from './share.js';
import { SOCIAL_ARCHETYPE_SVG } from './share-templates.js';

/** Gives a raw <svg viewBox="0 0 240 180" ...>...</svg> string an explicit size for embedding inside the 1080x1350 result-card template. Mirrors positionIllo() in scripts/build-share-images.mjs (Task 3). */
function sizedIllo(svgString, w, h) {
  return svgString.replace('<svg ', '<svg width="' + w + '" height="' + h + '" preserveAspectRatio="xMidYMid meet" ');
}

const FAMS = ['A', 'B', 'C', 'D'];
const state = { path: null, idx: -1, answers: [] };

function el(id) {
  return document.getElementById(id);
}

function totalSteps() {
  return 1 + 12; // path select + 12 questions
}

function updateProgress() {
  const step = state.idx + 2; // path select = step 1
  const pct = Math.min(99, Math.round((step / (totalSteps() + 1)) * 100));
  const fill = el('aq-fill');
  if (fill) fill.style.width = pct + '%';
  const p = el('aq-pct');
  if (p) p.textContent = pct + '%';
  const bb = el('aq-back');
  if (bb) bb.style.visibility = state.idx >= 0 ? 'visible' : 'hidden';
}

function show(id) {
  document.querySelectorAll('.screen').forEach(function (s) {
    s.classList.remove('active');
  });
  const sc = el(id);
  if (sc) {
    sc.classList.add('active');
    sc.scrollTo(0, 0);
  }
  updateProgress();
}

/** Build the 12 question screens for the chosen path. */
function buildQuestions(path) {
  const stage = el('aq-stage');
  document.querySelectorAll('.screen[data-aq]').forEach(function (s) {
    s.remove();
  });
  QUESTIONS[path].forEach(function (q, qi) {
    const sc = document.createElement('div');
    sc.className = 'screen';
    sc.id = 'aq-q' + qi;
    sc.setAttribute('data-aq', '1');
    let html = '<h2 class="hed">' + q.title + '</h2><div class="choices choices--single">';
    q.options.forEach(function (opt, oi) {
      html +=
        '<div class="choice" data-f="' + FAMS[oi] + '" data-q="' + qi + '"><span class="ct">' + opt + '</span></div>';
    });
    html += '</div>';
    sc.innerHTML = html;
    stage.appendChild(sc);
  });
  stage.addEventListener('click', onChoice);
}

function onChoice(ev) {
  const c = ev.target.closest('.choice[data-f]');
  if (!c) return;
  const qi = parseInt(c.getAttribute('data-q'), 10);
  if (qi !== state.idx) return;
  c.parentElement.querySelectorAll('.choice').forEach(function (x) {
    x.classList.remove('sel');
  });
  c.classList.add('sel');
  state.answers[qi] = c.getAttribute('data-f');
  setTimeout(function () {
    if (state.idx !== qi) return;
    if (qi === 11) {
      runLoading();
    } else {
      state.idx = qi + 1;
      show('aq-q' + state.idx);
    }
  }, 420);
}

function pickPath(path) {
  state.path = path;
  state.answers = [];
  buildQuestions(path);
  state.idx = 0;
  show('aq-q0');
}

function goBack() {
  if (state.idx <= 0) {
    state.idx = -1;
    show('aq-path');
    return;
  }
  state.idx--;
  show('aq-q' + state.idx);
}

function runLoading() {
  state.idx = 12;
  show('aq-loading');
  const steps = ['aql0', 'aql1', 'aql2'];
  let i = 0;
  const tick = function () {
    if (i > 0) {
      const prev = el(steps[i - 1]);
      prev.classList.remove('act');
      prev.classList.add('done');
      prev.querySelector('.ls-dot').textContent = '✓';
    }
    if (i < steps.length) {
      el(steps[i]).classList.add('act');
      i++;
      setTimeout(tick, 800);
    } else {
      setTimeout(showResult, 400);
    }
  };
  tick();
}

function showResult() {
  const r = scoreArchetype(state.answers);
  const copy = RESULTS[state.path][r.family];
  const slug = ARCHETYPE_SLUGS[state.path][r.family];
  el('aq-emblem').textContent = EMBLEMS[state.path][r.family];
  el('aq-r-name').textContent = copy.name;
  el('aq-r-mirror').textContent = copy.mirror;
  el('aq-r-gift').textContent = copy.gift;

  const illoEl = el('aq-r-illo');
  const illoSvg = ARCH_ILLOS[ARCHETYPES[slug].illo];
  if (illoEl && illoSvg) {
    illoEl.setAttribute('data-illo', 'arch-' + slug);
    illoEl.innerHTML = illoSvg;
    const reduceMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduceMotion) {
      // Each illustration tags its primary stroke-drawn outline with class="trace" (may be
      // a <path> or, for the seeker's compass ring, a <circle> — querySelector('path') alone
      // missed that one, and grabbed a fill-only decorative shape for others). Fall back to
      // the first stroke-bearing path if an illustration is ever added without a .trace tag.
      let traceEls = illoEl.querySelectorAll('.trace');
      if (!traceEls.length) {
        const fallback = illoEl.querySelector('path:not([stroke="none"])');
        traceEls = fallback ? [fallback] : [];
      }
      traceEls.forEach(function (traceEl) {
        traceEl.classList.add('illo-trace-once');
      });
    }
  }

  const sec = el('aq-r-secondary');
  if (r.secondary) {
    sec.textContent = '+ a strong ' + RESULTS[state.path][r.secondary].name.replace(/^The /, '') + ' side';
    sec.style.display = '';
  } else {
    sec.style.display = 'none';
  }
  el('aq-ios').href = STORE_LINKS.ios;
  el('aq-gp').href = STORE_LINKS.android;

  const shareBtn = el('aq-share-mine');
  const sendBtn = el('aq-share-send');
  const supported =
    typeof navigator !== 'undefined' &&
    (typeof navigator.share === 'function' || (navigator.clipboard && typeof navigator.clipboard.writeText === 'function'));
  if (shareBtn && sendBtn) {
    shareBtn.closest('.share-row').style.display = supported ? '' : 'none';
  }
  if (supported && shareBtn) {
    shareBtn.onclick = function () {
      const secondarySlug = r.secondary ? ARCHETYPE_SLUGS[state.path][r.secondary] : '';
      const url = buildShareUrl('/archetypes/' + slug + '.html', { s: secondarySlug });
      const secondaryLine = r.secondary
        ? '…with a strong ' + RESULTS[state.path][r.secondary].name.replace(/^The /, '') + ' side.'
        : '';
      const text = r.secondary
        ? 'I’m ' +
          copy.name +
          ' ' +
          EMBLEMS[state.path][r.family] +
          ' (with a ' +
          RESULTS[state.path][r.secondary].name.replace(/^The /, '') +
          ' side). What are you?'
        : 'I’m ' + copy.name + ' ' + EMBLEMS[state.path][r.family] + '. What are you?';
      const finish = function (cardBlob) {
        shareResult({ title: 'I’m ' + copy.name, text: text, url: url, cardBlob: cardBlob }).then(function (result) {
          if (result === 'copied') flashCopied(shareBtn);
        });
      };
      if (illoSvg) {
        const cardSvg = SOCIAL_ARCHETYPE_SVG
          .split('{{ILLO}}').join(sizedIllo(illoSvg, 480, 360))
          .split('{{NAME}}').join(copy.name)
          .split('{{SECONDARY}}').join(secondaryLine)
          .split('{{URL}}').join('mindlifthere.com/archetypes/' + slug + '.html');
        renderCard(cardSvg).then(finish).catch(function () { finish(); });
      } else {
        finish();
      }
    };
  }
  if (supported && sendBtn) {
    sendBtn.onclick = function () {
      const complementFam = COMPLEMENT[r.family];
      const complementName = RESULTS[state.path][complementFam].name.replace(/^The /, '');
      const url = buildShareUrl('/archetype-quiz.html', { src: 'friend' });
      const text = 'I got ' + copy.name + '. I bet you’re a ' + complementName + ' — 12 questions:';
      shareResult({ text: text, url: url }).then(function (result) {
        if (result === 'copied') flashCopied(sendBtn);
      });
    };
  }

  const tb = el('aq-topbar');
  if (tb) tb.style.display = 'none';
  show('aq-result');
}

function init() {
  const pWrap = el('aq-paths');
  Object.keys(PATHS).forEach(function (key) {
    const b = document.createElement('div');
    b.className = 'choice';
    b.innerHTML = '<span class="ci">' + PATHS[key].emoji + '</span><span class="ct">' + PATHS[key].label + '</span>';
    b.addEventListener('click', function () {
      pickPath(key);
    });
    pWrap.appendChild(b);
  });
  el('aq-back').addEventListener('click', goBack);
  updateProgress();
}

export { init, state };
